import React from "react";
import { Link as RouterLink } from "react-router-dom";

/* ── Card ── */
export function Card({ children, className = "", ...props }) {
  return <div className={`card ${className}`} {...props}>{children}</div>;
}
export function CardHeader({ children, className = "", ...props }) {
  return <div className={`card-header ${className}`} {...props}>{children}</div>;
}
export function CardBody({ children, className = "", ...props }) {
  return <div className={`card-body ${className}`} {...props}>{children}</div>;
}
export function CardFooter({ children, className = "", ...props }) {
  return <div className={`card-footer ${className}`} {...props}>{children}</div>;
}

/* ── Button ── */
export function Button({
  children, variant = "primary", size, loading, className = "", disabled, ...props
}) {
  const cls = [
    variant === "primary" ? "btn-primary" :
    variant === "secondary" ? "btn-secondary" :
    variant === "ghost" ? "btn-ghost" :
    variant === "danger" ? "btn-danger" :
    variant === "icon" ? "btn-icon" : "btn-primary",
    size === "sm" ? "btn-sm" : size === "lg" ? "btn-lg" : "",
    className,
  ].filter(Boolean).join(" ");

  return (
    <button className={cls} disabled={disabled || loading} {...props}>
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  );
}

/* ── Badge ── */
export function Badge({ children, variant = "brand", className = "", ...props }) {
  return (
    <span className={`badge-${variant} ${className}`} {...props}>
      {children}
    </span>
  );
}

/* ── Input ── */
export function Input({ error, className = "", ...props }) {
  return (
    <input className={`input ${error ? "input-error" : ""} ${className}`} {...props} />
  );
}

export function Textarea({ error, className = "", ...props }) {
  return (
    <textarea className={`input ${error ? "input-error" : ""} ${className}`} {...props} />
  );
}

export function Select({ error, className = "", children, ...props }) {
  return (
    <select className={`input ${error ? "input-error" : ""} ${className}`} {...props}>
      {children}
    </select>
  );
}

export function InputGroup({ label, hint, error, children, className = "" }) {
  return (
    <div className={`input-group ${className}`}>
      {label && <label className="input-label">{label}</label>}
      {children}
      {hint && !error && <p className="input-hint">{hint}</p>}
      {error && <p className="input-error-msg">{error}</p>}
    </div>
  );
}

/* ── Spinner ── */
export function Spinner({ size = "md" }) {
  const cls = `spinner spinner-${size}`;
  return <span className={cls} />;
}

/* ── ProgressBar ── */
export function ProgressBar({ value = 0, max = 100, variant = "" }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="progress-track">
      <div
        className={`progress-fill ${variant}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/* ── Alert ── */
export function Alert({ variant = "info", children, className = "", ...props }) {
  return (
    <div className={`alert-${variant} ${className}`} {...props}>
      {children}
    </div>
  );
}

/* ── StatCard ── */
export function StatCard({
  label, value, sub, icon: Icon, iconColor,
  progress, progressMax, progressVariant,
  className = "",
}) {
  return (
    <Card className={`stat-card ${className}`}>
      <CardBody>
        {Icon && (
          <div
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-md border border-brand-100"
            style={{ background: "rgba(1,113,222,0.1)" }}
          >
            <Icon size={15} color={iconColor || "var(--color-brand)"} />
          </div>
        )}
        <p className="stat-label">{label}</p>
        <p className="stat-value">{value}</p>
        {sub && <p className="stat-sub">{sub}</p>}
        {progressMax != null && (
          <div className="mt-3">
            <div className="flex justify-between text-2xs text-ink-faint mb-1.5">
              <span>Used</span>
              <span>{progress}/{progressMax}</span>
            </div>
            <ProgressBar value={progress} max={progressMax} variant={progressVariant} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ── PageHeader ── */
export function PageHeader({ title, subtitle, children, className = "", guideUrl = "" }) {
  return (
    <div className={`page-header ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="page-title">{title}</h1>
            {guideUrl && (
              <RouterLink
                to={guideUrl}
                className="group relative text-ink-muted hover:text-brand transition-colors"
                aria-label={`View ${title} guide`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0ZM9 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM6.75 8a.75.75 0 0 0 0 1.5h.75v1.75a.75.75 0 0 0 1.5 0v-2.5A.75.75 0 0 0 8.25 8h-1.5Z" clipRule="evenodd" />
                </svg>
                <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-ink-primary px-2.5 py-1.5 text-2xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                  View guide
                </span>
              </RouterLink>
            )}
          </div>
          {subtitle && <p className="page-subtitle">{subtitle}</p>}
        </div>
        {children && <div className="flex items-center gap-2">{children}</div>}
      </div>
    </div>
  );
}

/* ── Table ── */
export function TableWrapper({ children, className = "" }) {
  return <div className={`table-wrapper ${className}`}>{children}</div>;
}

export function Table({ children, className = "", ...props }) {
  return <table className={`table ${className}`} {...props}>{children}</table>;
}

export function Th({ children, className = "", ...props }) {
  return <th className={className} {...props}>{children}</th>;
}

export function Td({ children, className = "", ...props }) {
  return <td className={className} {...props}>{children}</td>;
}

/* ── Misc ── */
export function IconBadge({ icon: Icon, size = 15, color = "var(--color-brand)", bg = "rgba(1,113,222,0.1)" }) {
  return (
    <div
      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-brand-100"
      style={{ background: bg }}
    >
      <Icon size={size} color={color} />
    </div>
  );
}

export function Skeleton({ className = "h-4 w-full" }) {
  return <div className={`skeleton ${className}`} />;
}

export function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Spinner size="lg" />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && (
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg border border-brand-100"
          style={{ background: "rgba(1,113,222,0.1)" }}>
          <Icon size={20} color="var(--color-brand)" />
        </div>
      )}
      <p className="text-sm font-semibold text-ink-primary mb-1">{title}</p>
      {description && <p className="text-xs text-ink-muted max-w-xs">{description}</p>}
    </div>
  );
}

export function Divider({ className = "" }) {
  return <div className={`divider ${className}`} />;
}
